import { Trash2 } from "lucide-react"
import type { CSSProperties, PointerEvent } from "react"
import type { BBox, ResizeHandle } from "@/lib/sketch-geometry"
import { hairlinePx } from "@/lib/sketch-viewport"
import { cn } from "@/lib/utils"

const HANDLES: ResizeHandle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"]
const DELETE_GAP_X_PX = 6
const DELETE_NUDGE_Y_PX = 4

type Props = {
  bbox: BBox
  showFrame?: boolean
  showHandles?: boolean
  showDelete?: boolean
  deleteLabel: string
  /** Camera zoom; handles/delete stay screen-sized via 1/zoom. */
  zoom?: number
  onDelete: () => void
  onBodyPointerDown: (e: PointerEvent) => void
  onHandlePointerDown: (handle: ResizeHandle, e: PointerEvent) => void
}

function handleStyle(handle: ResizeHandle, bbox: BBox, zoom: number): CSSProperties {
  const midX = bbox.x + bbox.w / 2
  const midY = bbox.y + bbox.h / 2
  const positions: Record<ResizeHandle, { left: number; top: number; cursor: string }> = {
    nw: { left: bbox.x, top: bbox.y, cursor: "nwse-resize" },
    n: { left: midX, top: bbox.y, cursor: "ns-resize" },
    ne: { left: bbox.x + bbox.w, top: bbox.y, cursor: "nesw-resize" },
    e: { left: bbox.x + bbox.w, top: midY, cursor: "ew-resize" },
    se: { left: bbox.x + bbox.w, top: bbox.y + bbox.h, cursor: "nwse-resize" },
    s: { left: midX, top: bbox.y + bbox.h, cursor: "ns-resize" },
    sw: { left: bbox.x, top: bbox.y + bbox.h, cursor: "nesw-resize" },
    w: { left: bbox.x, top: midY, cursor: "ew-resize" },
  }
  const p = positions[handle]
  const isCorner = handle.length === 2
  const inv = zoom > 0 ? 1 / zoom : 1
  return {
    left: p.left,
    top: p.top,
    cursor: p.cursor,
    width: isCorner ? 8 : handle === "n" || handle === "s" ? 14 : 6,
    height: isCorner ? 8 : handle === "e" || handle === "w" ? 14 : 6,
    transform: `translate(-50%, -50%) scale(${inv})`,
  }
}

export function SketchSelectionOverlay({
  bbox,
  showFrame = true,
  showHandles = true,
  showDelete = true,
  deleteLabel,
  zoom = 1,
  onDelete,
  onBodyPointerDown,
  onHandlePointerDown,
}: Props) {
  if (bbox.w < 1 || bbox.h < 1) return null

  const inv = zoom > 0 ? 1 / zoom : 1

  return (
    <div className="pointer-events-none absolute inset-0 z-30">
      {showFrame ? (
        <div
          className="pointer-events-auto absolute border border-ui-accent"
          style={{
            left: bbox.x,
            top: bbox.y,
            width: bbox.w,
            height: bbox.h,
            cursor: "move",
            borderWidth: hairlinePx(zoom),
          }}
          onPointerDown={onBodyPointerDown}
        />
      ) : null}
      {showDelete ? (
        <button
          type="button"
          title={deleteLabel}
          aria-label={deleteLabel}
          className={cn(
            "pointer-events-auto absolute z-50 inline-flex h-6 w-6 items-center justify-center rounded-md border border-ui-border bg-ui-bg-elevated text-ui-text-secondary shadow-sm",
            "hover:bg-ui-surface-raised hover:text-ui-text-primary",
          )}
          style={{
            left: bbox.x + bbox.w + DELETE_GAP_X_PX * inv,
            top: Math.max(0, bbox.y - DELETE_NUDGE_Y_PX * inv),
            transform: `scale(${inv})`,
            transformOrigin: "top left",
          }}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onDelete()
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      ) : null}
      {showHandles
        ? HANDLES.map((handle) => {
            const style = handleStyle(handle, bbox, zoom)
            const isCorner = handle.length === 2
            return (
              <button
                key={handle}
                type="button"
                aria-label={handle}
                className={cn(
                  "pointer-events-auto absolute z-40 rounded-full border border-ui-accent bg-ui-bg-elevated shadow-sm",
                  !isCorner && "rounded-md",
                )}
                style={style}
                onPointerDown={(e) => {
                  e.stopPropagation()
                  onHandlePointerDown(handle, e)
                }}
              />
            )
          })
        : null}
    </div>
  )
}
